import { getAccessToken, type AuthSession, type User } from './auth-store'

export const API_BASE_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:8797'

interface ApiMeta {
  requestId: string
}

interface ApiEnvelope<TData> {
  data: TData
  meta: ApiMeta
}

interface ApiErrorEnvelope {
  error: {
    code: string
    message: string
    details?: unknown
  }
  meta?: ApiMeta
}

interface AuthPayload {
  user: User
  session: AuthSession
}

export interface VariantScores {
  voiceMatch: number
  readability: number
  novelty: number
}

export interface GeneratedVariant {
  id: string
  label: 'Safe' | 'Balanced' | 'Bold'
  content: string
  scores?: VariantScores
}

export interface GenerateCreateRequest {
  input: string
  inputType?: 'auto' | 'topic' | 'url' | 'source_text' | 'transcript'
  platform?: 'linkedin' | 'x' | 'newsletter'
  voiceProfileId?: string
}

export interface GenerateCreateResponse {
  generationId: string
  draftId: string
  variants: GeneratedVariant[]
}

export interface AnalyzeStructureRequest {
  referenceText: string
  sourceUrl?: string
  sourceType?: 'paste' | 'extension' | 'url'
}

export interface AnalyzeStructureResponse {
  analysisId: string
  hookType: string
  pacing: string
  payoffType: string
  whyItWorks: string
}

export interface RemixGenerateRequest {
  analysisId: string
  topic: string
  angle?: string
  voiceProfileId?: string
  platform?: 'linkedin' | 'x' | 'newsletter'
}

export interface RemixGenerateResponse extends GenerateCreateResponse {
  analysisSummary: {
    analysisId: string
    hookType: string
    pacing: string
    payoffType: string
  }
}

export type ReferenceGenerateRequest = RemixGenerateRequest
export type ReferenceGenerateResponse = RemixGenerateResponse

export interface VoiceProfile {
  id: string
  name: string
  quality: 'learning' | 'good' | 'strong'
  isDefault: boolean
  sampleCount: number
  lastTrainedAt: string | null
}

export interface TemplateItem {
  id: string
  ownerUserId: string | null
  visibility: 'user' | 'curated'
  name: string
  tags: string[]
  structurePayload: Record<string, unknown>
  createdAt: string
  updatedAt: string
}

export interface DraftItem {
  id: string
  userId: string
  sourceGenerationId: string | null
  title: string | null
  content: string
  imageUrl?: string | null
  platform: 'linkedin' | 'x' | 'newsletter'
  status: 'draft' | 'scheduled' | 'published' | 'archived'
  lastSavedAt: string
  createdAt: string
  updatedAt: string
}

export interface DraftImageResponse {
  imageUrl: string
  provider: string
  prompt: string
}

export interface CalendarEntry {
  id: string
  userId: string
  draftId: string
  scheduledAt: string
  timezone: string
  status: 'scheduled' | 'published' | 'canceled'
  createdAt: string
  updatedAt: string
}

export interface ReferralAttributionEvent {
  id: string
  referrerUserId: string
  referredUserId: string
  referralCode: string
  state: 'pending' | 'qualified' | 'rejected'
  firstPaidInvoiceAt: string | null
  holdExpiresAt: string | null
  qualifiedAt: string | null
  rejectionReason: string | null
  friendCouponRedeemedAt: string | null
  createdAt: string
  updatedAt: string
}

export interface ReferralSnapshot {
  referralCode: string
  referralLink: string
  milestone: {
    qualifiedCount: number
    discountRate: number
    nextTarget: number | null
  }
}

export interface ReferralProgress {
  qualifiedCount: number
  currentDiscountRate: number
  nextMilestoneTarget: number | null
  events: ReferralAttributionEvent[]
}

export interface CouponValidation {
  valid: boolean
  appliedRate: number
  duration: string
  effectivePrice: number
}

export interface CouponDefinition {
  id: string
  code: string
  couponType: 'promo' | 'friend_referral'
  discountMode: 'percent'
  discountValue: number
  durationMode: 'months' | 'once'
  durationMonths: number | null
  appliesToPlan: 'monthly' | 'yearly' | 'all'
  isActive: boolean
  endsAt: string | null
  createdAt: string
  updatedAt: string
}

export type DashboardActionPath = '/workspace' | '/audience' | '/insights'
export type DashboardLeadIntent = 'High' | 'Medium' | 'Low'
export type DashboardLeadStatus = 'new' | 'reviewing' | 'contacted'
export type DashboardSyncDomain = 'profile' | 'posts' | 'metrics' | 'responders'
export type DashboardTrendSeriesKey =
  | 'connections'
  | 'totalEngagements'
  | 'likes'
  | 'comments'
  | 'shares'

export interface DashboardAdvisorCard {
  id: string
  title: string
  rationale: string
  actionLabel: string
  actionPath: DashboardActionPath
  impact: string
}

export interface DashboardLead {
  id: string
  name: string
  headline: string
  company: string
  signalSummary: string
  intent: DashboardLeadIntent
  lastEngagedAt: string
  recommendedAction: string
  suggestedMessage: string
  recentPostTitle: string
  recentPostOutcome: string
  status: DashboardLeadStatus
  outreachMessage: string
  lastUpdatedAt: string
  score: number
  recencyScore?: number
  frequencyScore?: number
  reachOutRationale?: string
  topEngagedPosts?: Array<{
    activityUrn: string
    title: string
    engagementCount: number
    lastEngagedAt: string
  }>
}

export interface DashboardImpactDriverPost {
  id: string
  title: string
  publishedAt: string
  likes: number
  comments: number
  shares: number
  connectionDelta: number
  impactNote: string
}

export interface DashboardNextAction {
  id: 'draft' | 'schedule' | 'reply' | 'outreach' | 'refresh'
  title: string
  detail: string
  userLead: string
  ctaLabel: string
  to: DashboardActionPath
  impactLabel: string
  queueCategory: string
  queueMinutes: number
  rationale: string
  confidence: number
  expectedImpact: number
  sourceSignals: string[]
  timeframe: string
  freshness: DashboardActionFreshness
}

export interface DashboardQueueTask {
  id: string
  category: string
  title: string
  reason: string
  impact: string
  minutes: number
  cta: string
  to: DashboardActionPath
  confidence: number
  expectedImpact: number
}

export interface DashboardActionFreshness {
  isFresh: boolean
  staleDomains: DashboardSyncDomain[]
  lastSyncAt: string | null
}

export interface DashboardPlatformNextAction {
  title: string
  detail: string
  confidence: number
  expectedImpact: number
  sourceSignals: string[]
  timeframe: string
  freshness: DashboardActionFreshness
}

export interface DashboardTrendPoint {
  dateLabel: string
  connections: number
  likes: number
  comments: number
  shares: number
  totalEngagements: number
}

export interface DashboardSnapshot {
  generatedAt: string
  syncStatus: 'Healthy' | 'Needs attention'
  metrics: {
    scheduled: number
    published: number
    queue: number
    warmLeads: number
    highIntent: number
    responseNeeded: number
    cadenceTarget: number
  }
  cadenceCoverage: number
  cadenceGap: number
  immediateAction: DashboardNextAction
  userNextAction: {
    id: DashboardNextAction['id']
    title: string
    detail: string
    whatToDoNow: string
    ctaLabel: string
    to: DashboardActionPath
    rationale: string
    confidence: number
    expectedImpact: number
    sourceSignals: string[]
    timeframe: string
    freshness: DashboardActionFreshness
  }
  platformNextAction: DashboardPlatformNextAction | null
  systemNextSteps: string[]
  priorityQueue: DashboardQueueTask[]
  queueMinutes: number
  trend: {
    points: DashboardTrendPoint[]
    delta: Record<DashboardTrendSeriesKey, number>
  }
  impactDrivers: DashboardImpactDriverPost[]
  warmLeadRadar: DashboardLead[]
  responseInbox: DashboardLead[]
  advisorCards: DashboardAdvisorCard[]
}

export interface AudienceWorkspaceSnapshot {
  generatedAt: string
  counts: {
    new: number
    reviewing: number
    contacted: number
    highIntent: number
    total: number
  }
  leads: DashboardLead[]
}

export interface InsightsOverviewSnapshot {
  generatedAt: string
  performanceSnapshot: {
    scheduled: number
    published: number
    draftQueue: number
    cadenceScore: number
  }
  responders: {
    count: number
    items: DashboardLead[]
  }
  recommendedActions: {
    count: number
    items: DashboardAdvisorCard[]
  }
}

function createIdempotencyKey(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }

  return `${Date.now()}_${Math.random().toString(36).slice(2)}`
}

function shouldAttachJsonContentType(method: string, body: BodyInit | null | undefined): boolean {
  if (method === 'GET' || method === 'HEAD' || body == null) {
    return false
  }

  if (typeof FormData !== 'undefined' && body instanceof FormData) {
    return false
  }

  if (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams) {
    return false
  }

  if (typeof Blob !== 'undefined' && body instanceof Blob) {
    return false
  }

  if (typeof ArrayBuffer !== 'undefined' && body instanceof ArrayBuffer) {
    return false
  }

  return true
}

class ApiClient {
  private readonly baseUrl: string

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl
  }

  private async adminRequest<TData>(
    endpoint: string,
    adminToken: string,
    options: RequestInit = {}
  ): Promise<TData> {
    const headers = new Headers(options.headers)
    headers.set('x-admin-token', adminToken)

    return this.request<TData>(endpoint, {
      ...options,
      headers,
    })
  }

  private async promoterRequest<TData>(
    endpoint: string,
    promoterToken: string,
    options: RequestInit = {}
  ): Promise<TData> {
    const headers = new Headers(options.headers)
    headers.set('Authorization', `Bearer ${promoterToken}`)

    return this.request<TData>(endpoint, {
      ...options,
      headers,
    })
  }

  private async request<TData>(endpoint: string, options: RequestInit = {}): Promise<TData> {
    const method = options.method?.toUpperCase() ?? 'GET'
    const headers = new Headers(options.headers)
    if (!headers.has('Content-Type') && shouldAttachJsonContentType(method, options.body)) {
      headers.set('Content-Type', 'application/json')
    }

    if (method === 'POST' || method === 'PATCH' || method === 'PUT') {
      if (!headers.has('Idempotency-Key')) {
        headers.set('Idempotency-Key', createIdempotencyKey())
      }
    }

    const accessToken = getAccessToken()
    if (accessToken && !headers.has('Authorization')) {
      headers.set('Authorization', `Bearer ${accessToken}`)
    }

    let response: Response

    try {
      response = await fetch(`${this.baseUrl}${endpoint}`, {
        ...options,
        headers,
      })
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown network error'
      throw new Error(`Could not reach API (${this.baseUrl}): ${message}`)
    }

    if (response.status === 204) {
      return undefined as TData
    }

    const body = (await response.json()) as ApiEnvelope<TData> | ApiErrorEnvelope

    if (!response.ok) {
      const error = body as ApiErrorEnvelope
      throw new Error(error.error?.message ?? `Request failed with ${response.status}`)
    }

    return (body as ApiEnvelope<TData>).data
  }

  register(payload: {
    email: string
    password: string
    name?: string
    referralCode?: string
  }): Promise<AuthPayload> {
    return this.request<AuthPayload>('/auth/register', {
      method: 'POST',
      body: JSON.stringify(payload),
    })
  }

  login(payload: { email: string; password: string }): Promise<AuthPayload> {
    return this.request<AuthPayload>('/auth/login', {
      method: 'POST',
      body: JSON.stringify(payload),
    })
  }

  logout(): Promise<void> {
    return this.request<void>('/auth/logout', {
      method: 'POST',
      body: JSON.stringify({}),
    })
  }

  generateCreate(payload: GenerateCreateRequest): Promise<GenerateCreateResponse> {
    return this.request<GenerateCreateResponse>('/generate/create', {
      method: 'POST',
      body: JSON.stringify(payload),
    })
  }

  analyzeStructure(payload: AnalyzeStructureRequest): Promise<AnalyzeStructureResponse> {
    return this.request<AnalyzeStructureResponse>('/analysis/structure', {
      method: 'POST',
      body: JSON.stringify(payload),
    })
  }

  analyzeReferenceStructure(payload: AnalyzeStructureRequest): Promise<AnalyzeStructureResponse> {
    return this.analyzeStructure(payload)
  }

  getAnalysis(analysisId: string): Promise<{
    analysisId: string
    sourceType: string
    sourceUrl: string | null
    referenceText: string
    hookType: string
    pacing: string
    payoffType: string
    whyItWorks: string
    createdAt: string
  }> {
    return this.request(`/analysis/${analysisId}`)
  }

  generateRemix(payload: RemixGenerateRequest): Promise<RemixGenerateResponse> {
    return this.request<RemixGenerateResponse>('/generate/remix', {
      method: 'POST',
      body: JSON.stringify(payload),
    })
  }

  generateFromReference(payload: ReferenceGenerateRequest): Promise<ReferenceGenerateResponse> {
    return this.generateRemix(payload)
  }

  extensionAnalyze(payload: { referenceText?: string; sourceUrl?: string }): Promise<{
    accepted: boolean
    analysisId?: string
    handoffUrl?: string
    fallbackUrl?: string
  }> {
    return this.request('/extension/analyze', {
      method: 'POST',
      body: JSON.stringify(payload),
    })
  }

  adminOverview(
    adminToken: string,
    params?: { from?: string; to?: string }
  ): Promise<{
    kpis: {
      visitors: number
      signups: number
      logins: number
      paidConversions: number
      mrr: number
    }
    alerts: Array<{
      id: string
      type: string
      severity: 'low' | 'medium' | 'high'
      message: string
    }>
    totalUsers: number
    totalSignups: number
    totalLogins: number
    totalGenerations: number
    trialActiveUsers: number
    paidUsers: number
  }> {
    const query = new URLSearchParams()
    if (params?.from) {
      query.set('from', params.from)
    }
    if (params?.to) {
      query.set('to', params.to)
    }

    const qs = query.toString()
    return this.adminRequest(
      qs.length > 0 ? `/admin/overview?${qs}` : '/admin/overview',
      adminToken
    )
  }

  adminAcquisitionFunnel(
    adminToken: string,
    params?: { from?: string; to?: string; granularity?: 'day' | 'week' | 'month' }
  ): Promise<{
    stages: Array<{
      name:
        | 'landing'
        | 'signup_completed'
        | 'first_generation'
        | 'first_schedule'
        | 'paid_conversion'
      count: number
    }>
  }> {
    const query = new URLSearchParams()
    if (params?.from) {
      query.set('from', params.from)
    }
    if (params?.to) {
      query.set('to', params.to)
    }
    if (params?.granularity) {
      query.set('granularity', params.granularity)
    }

    const qs = query.toString()
    return this.adminRequest(
      qs.length > 0 ? `/admin/acquisition/funnel?${qs}` : '/admin/acquisition/funnel',
      adminToken
    )
  }

  adminSignups(
    adminToken: string,
    params?: { limit?: number; source?: string; country?: string; promoterId?: string }
  ): Promise<{
    items: Array<{
      id: string
      userId: string
      email: string
      referralCode: string | null
      firstTouchSource: string | null
      firstTouchCampaign: string | null
      countryCode: string | null
      regionCode: string | null
      promoterId: string | null
      promoterLinkId: string | null
      createdAt: string
    }>
  }> {
    const query = new URLSearchParams()
    query.set('limit', String(params?.limit ?? 100))
    if (params?.source) {
      query.set('source', params.source)
    }
    if (params?.country) {
      query.set('country', params.country)
    }
    if (params?.promoterId) {
      query.set('promoterId', params.promoterId)
    }

    return this.adminRequest(`/admin/signups?${query.toString()}`, adminToken)
  }

  adminLogins(
    adminToken: string,
    params?: { limit?: number; status?: 'success' | 'failed' }
  ): Promise<{
    items: Array<{
      id: string
      userId: string
      email: string
      occurredAt: string
      status: 'success' | 'failed'
      failureReason: string | null
      ipCountryCode: string | null
      ipRegionCode: string | null
      ipCity: string | null
      deviceType: string | null
    }>
  }> {
    const query = new URLSearchParams()
    query.set('limit', String(params?.limit ?? 100))
    if (params?.status) {
      query.set('status', params.status)
    }

    return this.adminRequest(`/admin/logins?${query.toString()}`, adminToken)
  }

  adminUserJourney(
    adminToken: string,
    userId: string
  ): Promise<{
    userId: string
    lifecycleState: string
    timeline: Array<{
      event: string
      at: string
      details?: Record<string, unknown>
    }>
    signupEvents: unknown[]
    loginEvents: unknown[]
    generations: unknown[]
    drafts: unknown[]
  }> {
    return this.adminRequest(`/admin/users/${encodeURIComponent(userId)}/journey`, adminToken)
  }

  adminUsers(
    adminToken: string,
    params?: { q?: string; state?: string; limit?: number }
  ): Promise<{
    items: Array<{
      userId: string
      email: string
      firstTouchSource: string | null
      countryCode: string | null
      createdAt: string
      lifecycleState: string
    }>
  }> {
    const query = new URLSearchParams()
    query.set('limit', String(params?.limit ?? 100))
    if (params?.q) {
      query.set('q', params.q)
    }
    if (params?.state) {
      query.set('state', params.state)
    }

    return this.adminRequest(`/admin/users?${query.toString()}`, adminToken)
  }

  adminListPromoters(
    adminToken: string,
    params?: { status?: 'active' | 'suspended' | 'revoked' }
  ): Promise<{
    items: Array<{
      id: string
      email: string
      name: string
      status: 'active' | 'suspended' | 'revoked'
      campaignLabel: string | null
      availableCredits: number
      nextRefreshAt: string
    }>
  }> {
    const query = new URLSearchParams()
    if (params?.status) {
      query.set('status', params.status)
    }

    const qs = query.toString()
    return this.adminRequest(
      qs.length > 0 ? `/admin/promoters?${qs}` : '/admin/promoters',
      adminToken
    )
  }

  adminCreatePromoter(
    adminToken: string,
    payload: {
      email: string
      name: string
      campaignLabel?: string
      refreshCredits?: number
      refreshIntervalDays?: number
    }
  ): Promise<{
    promoterId: string
    email: string
    name: string
    status: 'active' | 'suspended' | 'revoked'
    nextRefreshAt: string
    inviteCode: string
  }> {
    return this.adminRequest('/admin/promoters', adminToken, {
      method: 'POST',
      body: JSON.stringify(payload),
    })
  }

  adminGetPromoterDetail(
    adminToken: string,
    promoterId: string
  ): Promise<{
    promoterId: string
    email: string
    name: string
    status: 'active' | 'suspended' | 'revoked'
    campaignLabel: string | null
    credits: {
      balance: number
      nextRefreshAt: string
    }
    performance: {
      links: number
      signups: number
      paidConversions: number
      revenueInfluenced: number
    }
    inviteCode: string
    links: Array<{
      id: string
      code: string
      url: string
      signups: number
      paidConversions: number
    }>
  }> {
    return this.adminRequest(`/admin/promoters/${encodeURIComponent(promoterId)}`, adminToken)
  }

  adminCreatePromoterCampaignLink(
    adminToken: string,
    promoterId: string,
    payload: {
      label?: string
      landingPath?: string
      campaignName?: string
      utmSource?: string
      utmMedium?: string
      utmCampaign?: string
    }
  ): Promise<{
    id: string
    code: string
    url: string
    signups: number
    paidConversions: number
  }> {
    return this.adminRequest(
      `/admin/promoters/${encodeURIComponent(promoterId)}/campaign-links`,
      adminToken,
      {
        method: 'POST',
        body: JSON.stringify(payload),
      }
    )
  }

  adminPromoterMetrics(
    adminToken: string,
    promoterId: string
  ): Promise<{
    promoterId: string
    links: Array<{
      id: string
      code: string
      url: string
      signups: number
      paidConversions: number
      revenueInfluenced: number
    }>
    signups: number
    paidConversions: number
    revenueInfluenced: number
  }> {
    return this.adminRequest(
      `/admin/promoters/${encodeURIComponent(promoterId)}/metrics`,
      adminToken
    )
  }

  adminSuspendPromoter(
    adminToken: string,
    promoterId: string,
    reason?: string
  ): Promise<{ id: string; status: 'active' | 'suspended' | 'revoked' }> {
    return this.adminRequest(
      `/admin/promoters/${encodeURIComponent(promoterId)}/suspend`,
      adminToken,
      {
        method: 'POST',
        body: JSON.stringify(reason ? { reason } : {}),
      }
    )
  }

  adminResumePromoter(
    adminToken: string,
    promoterId: string,
    reason?: string
  ): Promise<{ id: string; status: 'active' | 'suspended' | 'revoked' }> {
    return this.adminRequest(
      `/admin/promoters/${encodeURIComponent(promoterId)}/resume`,
      adminToken,
      {
        method: 'POST',
        body: JSON.stringify(reason ? { reason } : {}),
      }
    )
  }

  adminReferrals(
    adminToken: string,
    params?: { state?: 'pending' | 'qualified' | 'rejected'; limit?: number }
  ): Promise<{
    summary: { pending: number; qualified: number; rejected: number }
    items: ReferralAttributionEvent[]
  }> {
    const query = new URLSearchParams()
    query.set('limit', String(params?.limit ?? 200))
    if (params?.state) {
      query.set('state', params.state)
    }

    return this.adminRequest(`/admin/referrals?${query.toString()}`, adminToken)
  }

  adminReconcileReferrals(
    adminToken: string,
    payload: { reason: string; nowIso?: string }
  ): Promise<{
    evaluated: number
    newlyQualified: number
    stillPending: number
    rejected: number
    runAt: string
    reason: string | null
  }> {
    return this.adminRequest('/admin/referrals/reconcile', adminToken, {
      method: 'POST',
      body: JSON.stringify(payload),
    })
  }

  adminCoupons(
    adminToken: string,
    params?: { status?: 'active' | 'all' }
  ): Promise<{ items: CouponDefinition[] }> {
    const query = new URLSearchParams()
    if (params?.status && params.status !== 'all') {
      query.set('status', params.status)
    }

    const qs = query.toString()
    return this.adminRequest(qs.length > 0 ? `/admin/coupons?${qs}` : '/admin/coupons', adminToken)
  }

  adminCreateCoupon(
    adminToken: string,
    payload: {
      code: string
      couponType?: 'promo' | 'friend_referral'
      discountValue: number
      durationMode?: 'months' | 'once'
      durationMonths?: number
      appliesToPlan?: 'monthly' | 'yearly' | 'all'
      endsAt?: string
    }
  ): Promise<CouponDefinition> {
    return this.adminRequest('/admin/coupons', adminToken, {
      method: 'POST',
      body: JSON.stringify(payload),
    })
  }

  adminPatchCoupon(
    adminToken: string,
    couponId: string,
    payload: { isActive?: boolean; endsAt?: string | null }
  ): Promise<CouponDefinition> {
    return this.adminRequest(`/admin/coupons/${encodeURIComponent(couponId)}`, adminToken, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    })
  }

  promoterLogin(payload: { email: string; inviteCode: string }): Promise<{
    promoter: {
      id: string
      email: string
      name: string
      status: 'active' | 'suspended' | 'revoked'
    }
    session: {
      accessToken: string
    }
  }> {
    return this.request('/promoter/login', {
      method: 'POST',
      body: JSON.stringify(payload),
    })
  }

  promoterMe(promoterToken: string): Promise<{
    promoterId: string
    email: string
    name: string
    status: 'active' | 'suspended' | 'revoked'
    campaignLabel: string | null
  }> {
    return this.promoterRequest('/promoter/me', promoterToken)
  }

  promoterCredits(promoterToken: string): Promise<{
    balance: number
    nextRefreshAt: string
    policyType: string
    ledger: Array<{
      id: string
      eventType: 'refresh' | 'top_up' | 'debit'
      delta: number
      balanceAfter: number
      reason: string
      createdAt: string
    }>
  }> {
    return this.promoterRequest('/promoter/credits', promoterToken)
  }

  promoterCreateLink(
    promoterToken: string,
    payload: {
      landingPath: string
      campaignName: string
      utmSource: string
      utmMedium: string
      utmCampaign: string
    }
  ): Promise<{
    link: {
      id: string
      code: string
      url: string
      signups: number
      paidConversions: number
    }
    balance: number
    ledgerEntryId: string
  }> {
    return this.promoterRequest('/promoter/links', promoterToken, {
      method: 'POST',
      body: JSON.stringify(payload),
    })
  }

  listVoiceProfiles(): Promise<{ items: VoiceProfile[] }> {
    return this.request<{ items: VoiceProfile[] }>('/voice-profiles')
  }

  createVoiceProfile(payload: { name: string; isDefault?: boolean }): Promise<VoiceProfile> {
    return this.request<VoiceProfile>('/voice-profiles', {
      method: 'POST',
      body: JSON.stringify(payload),
    })
  }

  updateVoiceProfile(
    profileId: string,
    payload: { name?: string; isDefault?: boolean }
  ): Promise<VoiceProfile> {
    return this.request<VoiceProfile>(`/voice-profiles/${profileId}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    })
  }

  listTemplates(params?: {
    q?: string
    tag?: string
    visibility?: 'user' | 'curated'
  }): Promise<{ items: TemplateItem[] }> {
    const query = new URLSearchParams()

    if (params?.q) {
      query.set('q', params.q)
    }

    if (params?.tag) {
      query.set('tag', params.tag)
    }

    if (params?.visibility) {
      query.set('visibility', params.visibility)
    }

    const qs = query.toString()
    return this.request<{ items: TemplateItem[] }>(qs ? `/templates?${qs}` : '/templates')
  }

  createTemplate(payload: {
    name: string
    tags: string[]
    structurePayload: Record<string, unknown>
  }): Promise<TemplateItem> {
    return this.request<TemplateItem>('/templates', {
      method: 'POST',
      body: JSON.stringify(payload),
    })
  }

  deleteTemplate(templateId: string): Promise<void> {
    return this.request<void>(`/templates/${templateId}`, {
      method: 'DELETE',
    })
  }

  listDrafts(params?: {
    status?: 'draft' | 'scheduled' | 'published' | 'archived'
    limit?: number
  }): Promise<{ items: DraftItem[] }> {
    const query = new URLSearchParams()

    if (params?.status) {
      query.set('status', params.status)
    }

    if (params?.limit) {
      query.set('limit', String(params.limit))
    }

    const qs = query.toString()
    return this.request<{ items: DraftItem[] }>(qs ? `/drafts?${qs}` : '/drafts')
  }

  getDraft(draftId: string): Promise<{
    id: string
    title: string | null
    content: string
    status: string
    versionsSummary: { count: number; latestVersionNo: number }
  }> {
    return this.request(`/drafts/${draftId}`)
  }

  updateDraft(
    draftId: string,
    payload: { content?: string; title?: string; imageUrl?: string | null }
  ): Promise<{
    draft: DraftItem
    versionId: string
    versionNo: number
  }> {
    return this.request(`/drafts/${draftId}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    })
  }

  scheduleDraft(
    draftId: string,
    payload?: { scheduledAt?: string; timezone?: string }
  ): Promise<{
    scheduled: boolean
    scheduledAt: string
    calendarEntryId: string
  }> {
    return this.request(`/drafts/${draftId}/schedule`, {
      method: 'POST',
      body: JSON.stringify(payload ?? {}),
    })
  }

  publishDraft(draftId: string): Promise<{
    published: boolean
    publishedAt: string
    draft: DraftItem
  }> {
    return this.request(`/drafts/${draftId}/publish`, {
      method: 'POST',
      body: JSON.stringify({}),
    })
  }

  generateDraftImage(payload: {
    prompt: string
    aspectRatio?: '1:1' | '16:9' | '4:5'
  }): Promise<DraftImageResponse> {
    return this.request('/images/generate', {
      method: 'POST',
      body: JSON.stringify(payload),
    })
  }

  getCalendar(params?: { from?: string; to?: string }): Promise<{ items: CalendarEntry[] }> {
    const query = new URLSearchParams()

    if (params?.from) {
      query.set('from', params.from)
    }

    if (params?.to) {
      query.set('to', params.to)
    }

    const qs = query.toString()
    return this.request<{ items: CalendarEntry[] }>(qs ? `/calendar?${qs}` : '/calendar')
  }

  getSubscription(): Promise<{
    state: string
    plan: 'monthly' | 'yearly'
    trialEndsAt: string | null
    nextBillingAt: string | null
    currentPrice: {
      currency: 'USD'
      list: number
      effective: number
      milestoneDiscountRate: number
    }
  }> {
    return this.request('/subscription')
  }

  referralMe(): Promise<ReferralSnapshot> {
    return this.request('/referrals/me')
  }

  referralProgress(): Promise<ReferralProgress> {
    return this.request('/referrals/progress')
  }

  referralEvents(params?: { limit?: number }): Promise<{ items: ReferralAttributionEvent[] }> {
    const query = new URLSearchParams()
    query.set('limit', String(params?.limit ?? 100))
    return this.request(`/referrals/events?${query.toString()}`)
  }

  regenerateReferralLink(): Promise<{ referralCode: string; referralLink: string }> {
    return this.request('/referrals/link/regenerate', {
      method: 'POST',
      body: JSON.stringify({}),
    })
  }

  validateCoupon(payload: {
    plan: 'monthly' | 'yearly'
    couponCode?: string
    referralCode?: string
  }): Promise<CouponValidation> {
    return this.request('/coupons/validate', {
      method: 'POST',
      body: JSON.stringify(payload),
    })
  }

  getCredits(): Promise<{
    balance: number
    entries: Array<{
      id: string
      delta: number
      balanceAfter: number
      reason: string
      createdAt: string
    }>
  }> {
    return this.request('/credits')
  }

  getTrial(): Promise<{
    state: 'not_started' | 'active' | 'expired'
    daysLeft: number
    startedAt: string | null
    endsAt: string | null
  }> {
    return this.request('/trial/me')
  }

  startTrial(): Promise<{
    state: string
    trialDays: number
    startedAt: string | null
    endsAt: string | null
    trigger: string | null
  }> {
    return this.request('/trial/start', {
      method: 'POST',
      body: JSON.stringify({
        trigger: 'signup',
      }),
    })
  }

  createCheckout(payload: { plan: 'monthly' | 'yearly'; couponCode?: string }): Promise<{
    checkoutUrl: string
    expiresAt: string
    plan: 'monthly' | 'yearly'
    amount: number
    couponCode?: string
    referralCode?: string
    friendCouponAutoApplied?: boolean
    appliedDiscountRate?: number
    discountDuration?: string
  }> {
    return this.request('/billing/checkout', {
      method: 'POST',
      body: JSON.stringify(payload),
    })
  }

  changePlan(payload: {
    targetPlan: 'monthly' | 'yearly'
    effectiveMode?: 'immediate' | 'next_cycle'
  }): Promise<{
    changeAccepted: boolean
    effectiveAt: string
    prorationCredit: number
    state: string
    plan: 'monthly' | 'yearly'
  }> {
    return this.request('/billing/plan-change', {
      method: 'POST',
      body: JSON.stringify(payload),
    })
  }

  getConsent(): Promise<{
    analyticsAllowed: boolean
    marketingAllowed: boolean
    functionalAllowed: boolean
    updatedAt: string
  }> {
    return this.request('/consent')
  }

  updateConsent(payload: {
    analyticsAllowed: boolean
    marketingAllowed: boolean
    functionalAllowed: boolean
  }): Promise<{
    analyticsAllowed: boolean
    marketingAllowed: boolean
    functionalAllowed: boolean
    updatedAt: string
  }> {
    return this.request('/consent', {
      method: 'POST',
      body: JSON.stringify(payload),
    })
  }

  getDashboardSnapshot(): Promise<DashboardSnapshot> {
    return this.request('/insights/dashboard')
  }

  getInsightsOverview(): Promise<InsightsOverviewSnapshot> {
    return this.request('/insights/overview')
  }

  getAudienceWorkspace(): Promise<AudienceWorkspaceSnapshot> {
    return this.request('/insights/audience')
  }

  updateAudienceLead(
    leadId: string,
    payload: {
      status?: DashboardLeadStatus
      outreachMessage?: string
      expectedLastUpdatedAt?: string
    }
  ): Promise<{
    lead: DashboardLead
    workspace: AudienceWorkspaceSnapshot
  }> {
    return this.request(`/insights/audience/${encodeURIComponent(leadId)}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    })
  }

  health(): Promise<{
    status: string
    app: string
    version: string
    storageMode: string
    timestamp: string
  }> {
    return this.request('/health')
  }
}

export const api = new ApiClient(API_BASE_URL)
